import React, { useRef } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const utahData = "/img/headerlogo.png";
const bodex = "/img/bodexLogo.png";
const kpi = "/img/KPI-DASH 2.png";
const pdfIcon = "/img/pdf1.png";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

export default function ConstructionTable18({ data = [] }) {
  const pdfRef = useRef();

  if (!Array.isArray(data) || data.length === 0) {
    return <p>No data available</p>;
  }

  // Extract years dynamically (numeric keys)
  const years = Object.keys(data[0]).filter((k) => !isNaN(k));

  // Colors for lines
  const colors = ["#F8BBD0", "#80DEEA", "#FFAB91", "#B7950B", "#9575CD"];

  // Prepare chart datasets
  const datasets = data.map((record, idx) => {
    const values = years.map((y) => parseInt(record[y] || 0));
    return {
      label: record.education_label,
      data: values,
      borderColor: colors[idx % colors.length],
      backgroundColor: colors[idx % colors.length],
      fill: false,
      tension: 0.3,
      pointRadius: 4,
    };
  });

  const chartData = { labels: years, datasets };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" },
      title: {
        display: true,
        text: "Employment by Education Level (2018–2024)",
        font: { size: 16, weight: "bold" },
      },
      tooltip: {
        callbacks: {
          label: (context) =>
            `${context.dataset.label}: ${context.raw.toLocaleString("en-US")}`,
        },
      },
    },
    scales: {
      x: { title: { display: true, text: "Year" } },
      y: {
        beginAtZero: true,
        title: { display: true, text: "Employment Count" },
        ticks: {
          callback: function (value) {
            return value.toLocaleString("en-US");
          },
        },
      },
    },
  };

  // 📊 Insights
  const totalByEducation = data.map((rec) =>
    years.reduce((sum, y) => sum + parseInt(rec[y] || 0), 0)
  );
  const overallTotal = totalByEducation.reduce((a, b) => a + b, 0);
  const topIndex = totalByEducation.indexOf(Math.max(...totalByEducation));
  const avgGrowth =
    ((totalByEducation[topIndex] - totalByEducation[0]) / totalByEducation[0]) *
    100;

  // 🖨️ PDF Export
  const downloadPDF = () => {
    const input = pdfRef.current;

    html2canvas(input, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
      scrollY: -window.scrollY,
    }).then((canvas) => {
      const pdf = new jsPDF("p", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pageWidth - 10;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      const imgData = canvas.toDataURL("image/png");

      let heightLeft = imgHeight;
      let pageNumber = 1;

      const addHeader = () => {
        pdf.addImage(utahData, "PNG", 60, 10, 32, 16);
        pdf.setFontSize(26);
        pdf.text("UTahData.org", 95, 22);
        pdf.link(50, 15, 60, 10, { url: "https://utahdata.org" });
        pdf.line(10, 35, 200, 35);
      };

      const addFooter = (pageNumber) => {
        pdf.setFontSize(9);
        const footerY = pageHeight - 10;
        pdf.line(10, footerY - 5, pageWidth - 10, footerY - 5);
        pdf.text(
          `Downloaded on: ${new Date().toLocaleDateString()}`,
          10,
          footerY
        );
        pdf.text(
          `Page ${pageNumber}`,
          pageWidth - 10,
          footerY,
          null,
          null,
          "right"
        );
      };

      addHeader();
      pdf.addImage(imgData, "PNG", 5, 32, imgWidth, imgHeight);
      heightLeft -= pageHeight - 32;

      while (heightLeft > 0) {
        pdf.addPage();
        pageNumber++;
        addHeader();
        pdf.addImage(
          imgData,
          "PNG",
          5,
          -(imgHeight - heightLeft) + 32,
          imgWidth,
          imgHeight
        );
        heightLeft -= pageHeight - 32;
        addFooter(pageNumber);
      }

      // Footer Branding
      pdf.line(10, 260, 200, 260);
      const footerY = 265;
      pdf.addImage(utahData, "PNG", 22, footerY, 30, 15);
      pdf.setFontSize(10);
      pdf.text("Built by", 55, footerY + 10);
      pdf.addImage(bodex, "PNG", 68, footerY + 4, 44, 10);
      pdf.text("using", 112, footerY + 10);
      pdf.addImage(kpi, "PNG", 125, footerY, 20, 20);
      pdf.text("20250916.v3.198", 148, footerY + 10);
      pdf.text(`Downloaded on: ${new Date().toLocaleDateString()}`, 150, 295);
      pdf.text(`From: ${window.location.href}`, 10, 295);

      pdf.save("employment_by_education_2018_2024.pdf");
    });
  };

  return (
    <>
      <div
        ref={pdfRef}
        className="p-6 bg-white from-indigo-50 via-white to-blue-50 rounded-xl shadow-md"
      >
        <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">
          Employment by Education Level (2018–2024)
        </h2>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 text-center">
          <div className="p-4 bg-blue-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Total Employment (All Levels)
            </p>
            <p className="text-xl font-bold text-blue-800">
              {overallTotal.toLocaleString("en-US")}
            </p>
          </div>
          <div className="p-4 bg-yellow-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Top Education Level
            </p>
            <p className="text-base font-semibold text-yellow-800">
              {data[topIndex].education_label}
            </p>
          </div>
          <div className="p-4 bg-green-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Approx. Growth (2018–2024)
            </p>
            <p className="text-xl font-bold text-green-800">
              {avgGrowth.toFixed(1)}%
            </p>
          </div>
        </div>

        {/* Table + Chart */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Table */}
          <div className="flex flex-col justify-center">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-center shadow-md rounded-lg overflow-hidden">
                <thead className="bg-gray-800 text-white">
                  <tr>
                    <th className="p-2 text-left">Education Level</th>
                    {years.map((year) => (
                      <th key={year} className="p-2" style={{ textAlign: 'right' }}>
                        {year}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.map((record, idx) => (
                    <tr
                      key={idx}
                      className={`${
                        idx === topIndex
                          ? "bg-yellow-50 font-semibold"
                          : "hover:bg-gray-100"
                      }`}
                    >
                      <td className="p-2 text-left">{record.education_label}</td>
                      {years.map((year) => (
                        <td key={year} className="p-2" style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                          {parseInt(record[year] || 0).toLocaleString("en-US")}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Chart */}
          <div className="flex flex-col w-full overflow-x-auto bg-white rounded-lg shadow-md">
            <div className="min-w-[400px] w-full h-96 p-4">
              <Line data={chartData} options={options} />
            </div>
          </div>
        </div>
      </div>

      {/* Download Button */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg transition-transform hover:scale-105"
        >
          <span>Download as PDF</span>
          <img className="w-[1.5rem]" src={pdfIcon} alt="pdf-icon" />
        </button>
      </div>
    </>
  );
}
